import {
  faChevronLeft,
  faChevronRight,
  faTimes,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { measureElement, useVirtualizer } from '@tanstack/react-virtual';
import classnames from 'classnames';
import React from 'react';
import { Button } from '../../flayr-components/Button/Button';
import { Flex } from '../../flayr-components/Flex/Flex';
import { Img } from '../../flayr-components/Img/Img';
import { ShowIf } from '../../flayr-components/ShowIf/ShowIf';
import Swipeable from '../Swipeable/Swipeable';

const AI_PILL_CLASS_NAME =
  'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-pink-100 text-pink-700 border border-pink-200 shadow-sm';

type TGalleryImage = string | {
  url: string;
  alt: string;
  matchedBy?: {
    skinTone?: boolean;
    ageBracket?: boolean;
  };
  bestMatch?: boolean;
};

interface IImgGalleryModalProps {
  images: TGalleryImage[];
  open?: boolean;
  arrowClassName?: string;
  className?: string;
  totalImages?: number;
  scrolling?: boolean;
  zIndex?: string;
  onClose?: (e?: any) => void;
  onNext?: (e?: any) => void;
  onPrev?: (e?: any) => void;
}

export const ImgGalleryModal: React.FC<React.PropsWithChildren<IImgGalleryModalProps>> = ({
  open,
  onClose,
  images,
  className = '',
  zIndex = '20',
  totalImages,
  onNext,
  onPrev,
  scrolling,
}) => {
  const [selectedIdx, setSelectedIdx] = React.useState<number>(0);
  const scrollParentRef = React.useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: images.length,
    enabled: open && !!scrolling,
    getScrollElement: () => scrollParentRef.current,
    estimateSize: () => 300,
    gap: 16,
    paddingStart: 16,
    paddingEnd: 32,
    overscan: 4,
    measureElement,
    getItemKey: (index) => {
      const item = images[index];
      const u = typeof item === 'string' ? item : item?.url;
      return `${u ?? 'img'}-${index}`;
    },
  });

  React.useLayoutEffect(() => {
    if (open && scrolling) {
      scrollParentRef.current?.scrollTo(0, 0);
      virtualizer.scrollToOffset(0);
    }
  }, [open, scrolling, images.length]);

  React.useEffect(() => {
    let html = document.querySelector('html');
    if (open) {
      html?.classList.add('overflow-hidden');
    }
    return () => {
      if (html?.classList.contains('overflow-hidden')) {
        html.classList.remove('overflow-hidden');
      }
    };
  }, [open]);

  React.useEffect(() => {
    if (open) {
      const fn = (e) => {
        switch (e.key) {
          case 'ArrowLeft':
            selectedIdx > 0 && handleNext(selectedIdx - 1);
            break;
          case 'ArrowRight':
            selectedIdx < images.length - 1 && handleNext(selectedIdx + 1);
            break;
          case 'Escape':
            onClose?.();
            break;
        }
      };
      document.addEventListener('keyup', fn);
      return () => {
        document.removeEventListener('keyup', fn);
      };
    }
  }, [open, selectedIdx, images]);

  // function handleListImgClick(e) {
  //   const id = e.id;
  //   setSelectedIdx(+id);
  // }

  function handleNext(idx) {
    onNext?.(idx);
    setSelectedIdx(idx);
  }

  function handlePrev(idx) {
    onPrev?.(idx);
    setSelectedIdx(idx);
  }

  if (!open) {
    return null;
  }

  return (
    <Flex
      className={`fixed top-0 left-0 w-full h-full min-h-0 ${className} z-${zIndex} `}
      column
      center
    >
      <div className="fixed top-0 left-0 flex w-full h-full bg-white" />
      <div className="sticky top-0 flex flex-col self-center w-full h-full min-h-0">
        <div className="relative z-10 flex items-center self-start w-full shrink-0 px-4 pt-3 pb-2">
          <Button onClick={onClose} className="ml-auto lg:mr-auto">
            <FontAwesomeIcon className="mr-2" icon={faTimes} />
            Close
          </Button>
          <div className="flex-grow text-sm text-center">
            <span className="inline-block -ml-24">
              <span hidden={scrolling}>
                {selectedIdx + 1} of {totalImages || images.length}
              </span>
              <span hidden={!scrolling}>{totalImages} Total</span>
            </span>
          </div>
        </div>

        {!scrolling && (
          <div className="flex min-h-0 flex-1 flex-col">
            <ImgSlider
              images={images}
              selected={selectedIdx}
              onNext={handleNext}
              onPrev={handlePrev}
            />
          </div>
        )}

        {scrolling && (
          <div
            ref={scrollParentRef}
            className="w-full min-h-0 flex-1 overflow-y-auto md:pt-2"
          >
            <div
              className="relative mx-auto w-full"
              style={{ height: `${virtualizer.getTotalSize()}px` }}
            >
              {virtualizer.getVirtualItems().map((virtualRow) => {
                const img = images[virtualRow.index];
                const src = typeof img === 'string' ? img : img.url;
                const pills: string[] = [];
                if (typeof img !== 'string' && img?.matchedBy?.skinTone) pills.push('Skin tone');
                if (typeof img !== 'string' && img?.matchedBy?.ageBracket) pills.push('Age');
                if (typeof img !== 'string' && img?.bestMatch) pills.push('Best match');
                return (
                  <div
                    key={virtualRow.key}
                    data-index={virtualRow.index}
                    ref={virtualizer.measureElement}
                    className="absolute left-0 top-0 w-full"
                    style={{
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    <div className="relative mx-auto w-11/12 lg:w-6/12">
                      <Img
                        src={src}
                        roundedCorners
                        shadow
                        responsive
                        sizes="(min-width: 1024px) 42vw, 90vw"
                        screenSizes={{
                          320: 360,
                          375: 400,
                          425: 440,
                          640: 480,
                          768: 560,
                          1024: 640,
                        }}
                      />
                      {!!pills.length && (
                        <div className="absolute left-3 top-3 z-10 flex flex-wrap gap-1.5">
                          {pills.map((pill) => (
                            <span
                              key={`${src}-${pill}`}
                              className={AI_PILL_CLASS_NAME}
                            >
                              {pill}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Flex>
  );
};

const ImgSlider: React.FC<React.PropsWithChildren<{
  images: TGalleryImage[];
  selected: number;
  totalImages?: number;
  onPrev?: (e?: any) => void;
  onNext?: (e?: any) => void;
}>> = ({ images, selected, totalImages, onNext, onPrev }) => {
  const [currentIdx, setCurrentIdx] = React.useState<number>(0);
  const isFirst = currentIdx < 1;
  const isLast = currentIdx >= images.length - 1;

  React.useEffect(() => {
    setCurrentIdx(selected);
  }, [selected]);

  function next() {
    if (currentIdx < images.length - 1) {
      const idx = currentIdx + 1;
      setCurrentIdx(idx);
      onNext?.(idx);
    }
  }
  function prev() {
    if (currentIdx > 0) {
      const idx = currentIdx - 1;
      setCurrentIdx(idx);
      onPrev?.(idx);
    }
  }

  return (
    <Swipeable
      className="flex items-stretch flex-grow"
      onSwipedRight={prev}
      onSwipedLeft={next}
    >
      <Flex
        className="relative z-20 flex-grow w-full pt-20 mx-auto lg:self-center lg:px-10 lg:items-center md:pt-0"
        x="between"
        y="start"
      >
        <div className="hidden w-16 text-center lg:block">
          {currentIdx !== 0 && (
            <Button onClick={prev} className="w-12 h-12" round>
              <FontAwesomeIcon icon={faChevronLeft} className="text-2xl" />
            </Button>
          )}
        </div>

        {(images as any).map((img, idx) => (
          <div
            key={idx}
            className={classnames(
              { hidden: idx !== currentIdx },
              'mx-auto max-w-xl xl:max-w-2xl px-8 w-full'
            )}
          >
            <ShowIf
              condition={typeof img === 'string'}
              elseTemplate={
                <div className="relative">
                  <Img
                    className="bg-gray-100"
                    src={img.url}
                    alt={img.alt}
                    loadingAnimation
                  />
                  {(img?.matchedBy?.skinTone || img?.matchedBy?.ageBracket || img?.bestMatch) && (
                    <div className="absolute top-3 left-3 flex flex-wrap gap-1.5 z-10">
                      {!!img?.matchedBy?.skinTone && (
                        <span className={AI_PILL_CLASS_NAME}>
                          Skin tone
                        </span>
                      )}
                      {!!img?.matchedBy?.ageBracket && (
                        <span className={AI_PILL_CLASS_NAME}>
                          Age
                        </span>
                      )}
                      {!!img?.bestMatch && (
                        <span className={AI_PILL_CLASS_NAME}>
                          Best match
                        </span>
                      )}
                    </div>
                  )}
                </div>
              }
            >
              <Img className="bg-gray-100" src={img} loadingAnimation />
            </ShowIf>
          </div>
        ))}

        <div className="hidden w-16 text-center lg:block">
          {currentIdx !== (totalImages || images.length) - 1 && (
            <Button className="w-12 h-12" round onClick={next}>
              <FontAwesomeIcon icon={faChevronRight} />
            </Button>
          )}
        </div>
      </Flex>
      <div className="absolute bottom-0 left-0 z-20 flex justify-center w-full pb-10 lg:hidden">
        <Button
          onClick={prev}
          className={classnames('w-12 h-12', { invisible: isFirst })}
          round
        >
          <FontAwesomeIcon icon={faChevronLeft} className="text-2xl" />
        </Button>
        <div className="w-10"></div>
        <Button
          className={classnames('w-12 h-12', {
            invisible: isLast,
          })}
          round
          onClick={next}
        >
          <FontAwesomeIcon icon={faChevronRight} className="text-2xl" />
        </Button>
      </div>
    </Swipeable>
  );
};
